export type GoogleOpeningPeriod = {
  open?: {
    day?: number;
    hour?: number;
    minute?: number;
  };
  close?: {
    day?: number;
    hour?: number;
    minute?: number;
  };
};

export const googleDayNamesRu = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
export const weekDayOrder = [1, 2, 3, 4, 5, 6, 0] as const;

export function isOpenAt(periods: unknown, date = new Date()): boolean {
  if (!Array.isArray(periods)) return false;

  const day = date.getDay();
  const minuteOfDay = date.getHours() * 60 + date.getMinutes();

  return periods.some((period) => {
    const item = period as GoogleOpeningPeriod;
    if (item.open?.day === undefined || item.close?.hour === undefined) {
      return false;
    }

    const openDay = item.open.day;
    const closeDay = item.close?.day ?? openDay;
    const openMinute = (item.open.hour ?? 0) * 60 + (item.open.minute ?? 0);
    const closeMinute = (item.close.hour ?? 0) * 60 + (item.close.minute ?? 0);

    if (openDay === closeDay && day === openDay) {
      return minuteOfDay >= openMinute && minuteOfDay < closeMinute;
    }

    if (day === openDay) {
      return minuteOfDay >= openMinute;
    }

    if (day === closeDay) {
      return minuteOfDay < closeMinute;
    }

    return false;
  });
}

export function formatTodayHours(periods: unknown, date = new Date()): string {
  if (!Array.isArray(periods) || periods.length === 0) {
    return "Не известно";
  }

  const day = date.getDay();
  const ranges = periods
    .map((period) => period as GoogleOpeningPeriod)
    .filter((period) => period.open?.day === day)
    .map((period) => {
      const open = formatTime(period.open?.hour, period.open?.minute);
      const close = formatTime(period.close?.hour, period.close?.minute);
      return `${open} - ${close}`;
    });

  return ranges.length > 0 ? ranges.join(", ") : "Закрыто";
}

export function formatTodayHoursWithDay(
  periods: unknown,
  date = new Date(),
): string {
  return `${formatDayName(date.getDay())} ${formatTodayHours(periods, date)}`;
}

export function formatWeekHours(
  periods: unknown,
  date = new Date(),
): Array<{ day: string; hours: string; isToday: boolean }> {
  if (!Array.isArray(periods) || periods.length === 0) {
    return weekDayOrder.map((day) => ({
      day: formatDayName(day),
      hours: "Не известно",
      isToday: day === date.getDay(),
    }));
  }

  return weekDayOrder.map((day) => {
    const ranges = periods
      .map((period) => period as GoogleOpeningPeriod)
      .filter((period) => period.open?.day === day)
      .map(
        (period) =>
          `${formatTime(period.open?.hour, period.open?.minute)} - ${formatTime(period.close?.hour, period.close?.minute)}`,
      );

    return {
      day: formatDayName(day),
      hours: ranges.length > 0 ? ranges.join(", ") : "Закрыто",
      isToday: day === date.getDay(),
    };
  });
}

function formatDayName(day: number): string {
  return googleDayNamesRu[day] ?? "";
}

function formatTime(hour = 0, minute = 0): string {
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}
